import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import "./Sidebar.css";
import "font-awesome/css/font-awesome.min.css";
import { useAuth } from "../common/AuthContext";

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const sidebarRef = useRef();
  const menuRef = useRef();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const isTouchDevice =
    "ontouchstart" in window || navigator.maxTouchPoints > 0;

  const openSidebar = () => setIsOpen(true);
  const closeSidebar = () => {
    setIsOpen(false);
    setActiveDropdown(null);
  };

  const toggleDropdown = (index) => {
    setActiveDropdown(activeDropdown === index ? null : index);
  };

  const handleLogout = () => {
    logout();
    closeSidebar();
    navigate("/login");
  };

  // ✅ Helper function for userPlan check
  const checkUserPlanAndNavigate = (path) => {
    const userPlan = localStorage.getItem("userPlan");
    if (userPlan && userPlan !== "null") {
      navigate(path);
    } else {
      navigate("/subscribe");
    }
    closeSidebar();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        closeSidebar();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  return (
    <>
      {/* Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <div
        className={`sidenav ${isOpen ? "open" : ""}`}
        ref={sidebarRef}
        onMouseLeave={() => !isTouchDevice && closeSidebar()}
      >
        {/* Logo */}
        <div className="sidebar-logo">
          <img
            src="/assets/images/noptionwave-logo-1.png"
            className="logo-image"
            alt="noptionwave logo"
          />
        </div>

        {/* Links */}
        <a href="dashboard" onClick={closeSidebar}>
          <i className="fa fa-info-circle"></i> DASHBOARD
        </a>

        {/* NSE Dropdown */}
        <div
          className={`dropdown-wrapper ${activeDropdown === 2 ? "active" : ""}`}
          onMouseEnter={() => !isTouchDevice && setActiveDropdown(2)}
          onMouseLeave={() => !isTouchDevice && setActiveDropdown(null)}
          onClick={() => isTouchDevice && toggleDropdown(2)}
        >
          <button className="dropdown-btn">
            <i className="fa fa-bar-chart"></i> NSE DATA{" "}
            <i className="fa fa-caret-down"></i>
          </button>
          <AnimatePresence>
            {activeDropdown === 2 && (
              <motion.div
                className="dropdown-container"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                {/* ✅ Added userPlan check before navigating */}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    checkUserPlanAndNavigate("/nse-option-chain");
                  }}
                >
                  <i className="fa fa-line-chart"></i> NSE OPTION CHAIN
                </a>

                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    checkUserPlanAndNavigate("/fifth-strike-option-chain");
                  }}
                >
                  <i className="fa fa-line-chart"></i> 5TH STRIKE DATA
                </a>

                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    checkUserPlanAndNavigate("/day-range-analysis");
                  }}
                >
                  <i className="fa fa-line-chart"></i> DAY RANGE ANALYSIS
                </a>

                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    checkUserPlanAndNavigate("/oi-data-analysis");
                  }}
                >
                  <i className="fa fa-line-chart"></i> OI DATA ANALYSIS
                </a>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* STOCK DATA Dropdown */}
        <div
          className={`dropdown-wrapper ${activeDropdown === 3 ? "active" : ""}`}
          onMouseEnter={() => !isTouchDevice && setActiveDropdown(3)}
          onMouseLeave={() => !isTouchDevice && setActiveDropdown(null)}
          onClick={() => isTouchDevice && toggleDropdown(3)}
        >
          <button className="dropdown-btn">
            <i className="fa fa-bar-chart"></i> STOCK DATA{" "}
            <i className="fa fa-caret-down"></i>
          </button>
          <AnimatePresence>
            {activeDropdown === 3 && (
              <motion.div
                className="dropdown-container"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                {/* ✅ Added userPlan check before navigating */}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    checkUserPlanAndNavigate("/stock-api-day-range-data");
                  }}
                >
                  <i className="fa fa-line-chart"></i> STOCK DAY RANGE DATA
                </a>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    checkUserPlanAndNavigate("/stock-data-analysis");
                  }}
                >
                  <i className="fa fa-line-chart"></i> STOCK DATA ANALYSIS
                </a>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* CRYPTO Dropdown */}
        <div
          className={`dropdown-wrapper ${activeDropdown === 1 ? "active" : ""}`}
          onMouseEnter={() => !isTouchDevice && setActiveDropdown(1)}
          onMouseLeave={() => !isTouchDevice && setActiveDropdown(null)}
          onClick={() => isTouchDevice && toggleDropdown(1)}
        >
          <button className="dropdown-btn">
            <i className="fa fa-bar-chart"></i> CRYPTO DATA{" "}
            <i className="fa fa-caret-down"></i>
          </button>
          <AnimatePresence>
            {activeDropdown === 1 && (
              <motion.div
                className="dropdown-container"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    checkUserPlanAndNavigate("/crypto-option-chain");
                  }}
                >
                  <i className="fa fa-line-chart"></i> CRYPTO OPTION CHAIN
                </a>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {user?.admin && (
          <a href="fyers-access-token" onClick={closeSidebar}>
            <i className="fa fa-key"></i> Fyers Access Token
          </a>
        )}

        {/* Other Links */}
        <a href="subscribe" onClick={closeSidebar}>
          <i className="fa fa-rupee"></i> Subscribe
        </a>
        <a href="#contact" onClick={closeSidebar}>
          <i className="fa fa-envelope"></i> Contact
        </a>
        <a href="#services" onClick={closeSidebar}>
          <i className="fa fa-cogs"></i> About
        </a>
        <a href="profile" onClick={closeSidebar}>
          <i className="fa fa-user-circle"></i> Profile
        </a>

        {/* User Profile */}
        <div className="user-profile">
          <div className="user-info">
            <i className="fa fa-user-circle fa-2x"></i>
            <div className="user-text">
              <strong>{user?.userName || "--"}</strong>
              <small>{user?.email || "--"}</small>
            </div>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            <i className="fa fa-sign-out"></i>
          </button>
        </div>
      </div>

      {/* Menu Button */}
      <button
        className="openbtn"
        ref={menuRef}
        onMouseEnter={() => !isTouchDevice && openSidebar()}
        onClick={() => isTouchDevice && openSidebar()}
        onTouchStart={() => isTouchDevice && openSidebar()}
      >
        ☰
      </button>
    </>
  );
};

export default Sidebar;
